import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Login as LoginIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailIcon,
} from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { GET_LOGIN_ACTIVITY_LOGS } from '../graphql/queries';

const formatTimestamp = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('bg-BG', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const LoginActivityTab = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchTerm.trim();
      setDebouncedSearch((prev) => {
        if (prev !== next) {
          setPage(0);
        }
        return next;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, loading, refetch } = useQuery(GET_LOGIN_ACTIVITY_LOGS, {
    variables: {
      page,
      size: rowsPerPage,
      search: debouncedSearch || null,
      status,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const pageData = data?.loginActivityLogs;
  const logs = pageData?.content || [];
  const totalElements = pageData?.totalElements || 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LoginIcon />
          Логове за вход
        </Typography>
        <IconButton onClick={() => refetch()} title="Обнови">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Кой е влизал успешно и кой е правил неуспешен опит, заедно с IP адреса.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Търсене по потребител, IP или фирма..."
          variant="outlined"
          size="small"
          sx={{ flex: 1, minWidth: 260 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={status}
          onChange={(_e, value) => {
            if (value) {
              setStatus(value);
              setPage(0);
            }
          }}
        >
          <ToggleButton value="ALL">Всички</ToggleButton>
          <ToggleButton value="SUCCESS">Успешни</ToggleButton>
          <ToggleButton value="FAILED">Неуспешни</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Дата и час</TableCell>
              <TableCell>Потребител</TableCell>
              <TableCell>Фирма</TableCell>
              <TableCell>IP адрес</TableCell>
              <TableCell align="center">Статус</TableCell>
              <TableCell>Причина</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">Няма записи за вход</Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTimestamp(row.timestamp)}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{row.username || '—'}</TableCell>
                  <TableCell>{row.companyName || '—'}</TableCell>
                  <TableCell>
                    <Tooltip title={row.userAgent || 'Няма User-Agent'} placement="top">
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {row.ipAddress || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    {row.success ? (
                      <Chip icon={<SuccessIcon />} label="Вход" color="success" size="small" />
                    ) : (
                      <Chip icon={<FailIcon />} label="Опит" color="error" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>{row.success ? '—' : (row.errorMessage || row.description || '—')}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalElements}
        page={page}
        onPageChange={(_e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Редове на страница:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} от ${count !== -1 ? count : `повече от ${to}`}`
        }
      />
    </Box>
  );
};

export default LoginActivityTab;
